// Room images live in src/assets/ — update imports if images move.
// Hotspot rects (xPct, yPct, wPct, hPct) are percent-based — tweak to realign.
import type { RoomDef } from './types';

import room1Img from '../../assets/room1.png';
import room2Img from '../../assets/room2.png';
import room3Img from '../../assets/room3.png';

export const rooms: RoomDef[] = [
  // ── Room 1: The Compression Room ──────────────────────────────────────────
  {
    id: 'room1',
    title: 'The Compression Room',
    narrative:
      'The room is full of symbols. Some are reality. Some are measurements pretending to be reality.',
    backgroundImage: room1Img,
    completionRule: { type: 'required-plus-min', required: ['r1_classify'], minTotal: 3 },
    puzzles: [
      {
        id: 'r1_scavenger',
        title: 'Examine the Room',
        narrative:
          'Click each object to examine it. Some hide glyphs — fragments you will need later.',
        params: {
          type: 'hotspot-scavenger',
          requiredVisitCount: 6,
          hotspots: [
            {
              id: 'hs_clock',
              label: 'Clock',
              kind: 'item',
              itemId: 'glyph_a',
              narrativeText:
                'The clock converts continuous time into discrete ticks. The numbers are real. The lived moment is also real — but different. A glyph falls out.',
              rect: { xPct: 3, yPct: 1, wPct: 26, hPct: 35 },
            },
            {
              id: 'hs_todo',
              label: 'To-do list',
              kind: 'clue',
              clueLabel: 'List',
              narrativeText:
                'Effort, complexity, and emotion compressed into text. Not the work — a symbol for it. Numbers hint at sequence.',
              rect: { xPct: 22, yPct: 14, wPct: 20, hPct: 24 },
            },
            {
              id: 'hs_deadline',
              label: 'Deadline note',
              kind: 'item',
              itemId: 'glyph_c',
              narrativeText:
                'A date on a calendar pointing to an imagined future. The urgency is real. The date is a pointer, not a presence. A glyph peels off.',
              rect: { xPct: 41, yPct: 10, wPct: 17, hPct: 22 },
            },
            {
              id: 'hs_headphones',
              label: 'Headphones',
              kind: 'clue',
              clueLabel: 'Phones',
              clueValue: 2,
              narrativeText:
                'Sound arriving at ears — no symbol required. The listening is the thing. The volume dial reads 2.',
              rect: { xPct: 30, yPct: 43, wPct: 30, hPct: 18 },
            },
            {
              id: 'hs_coffee',
              label: 'Coffee cup',
              kind: 'clue',
              clueLabel: 'Coffee',
              narrativeText:
                'Warmth, bitterness, and the physical sensation of drinking. Immediate, unmediated. Not a metric for anything.',
              rect: { xPct: 67, yPct: 58, wPct: 17, hPct: 20 },
            },
            {
              id: 'hs_journal',
              label: 'Open journal',
              kind: 'item',
              itemId: 'glyph_b',
              narrativeText:
                'Pen to paper, thought becoming word. The journal holds experience — it does not measure it. A glyph is pressed inside.',
              rect: { xPct: 8, yPct: 63, wPct: 54, hPct: 27 },
            },
            {
              id: 'hs_lamp',
              label: 'Desk lamp',
              kind: 'clue',
              clueLabel: 'Lamp',
              clueValue: 4,
              narrativeText:
                'A lamp with a wattage label: 40W. The first digit of power is 4.',
              rect: { xPct: 2, yPct: 28, wPct: 12, hPct: 25 },
            },
            {
              id: 'hs_bookshelf',
              label: 'Bookshelf',
              kind: 'clue',
              clueLabel: 'Books',
              clueValue: 7,
              narrativeText:
                '7 books stand in a row, each one a frame someone else built. Spine count: 7.',
              rect: { xPct: 65, yPct: 28, wPct: 18, hPct: 28 },
            },
          ],
        },
        hints: [
          'Click each object to examine it. Some reveal glyphs you will need later.',
          'The clock, deadline note, and open journal each hide a glyph.',
          'Three objects show numbers: the lamp (4), the bookshelf (7), and the headphones (2).',
        ],
      },
      {
        id: 'r1_pattern',
        title: 'Object Pattern',
        narrative:
          'Three objects in this room measure something. Identify them in the grid.',
        params: {
          type: 'pattern-match',
          gridIcons: [
            'clock', 'list-check', 'chart-bar',
            'headphones', 'mug-hot', 'book-open',
            'brain', 'heart', 'eye',
          ],
          correctSet: ['clock', 'list-check', 'chart-bar'],
          clueText: 'Select the 3 objects that measure or track something.',
        },
        hints: [
          'Think about which objects in the room convert experience into a number or symbol.',
          'Music, warmth, and thought are direct experiences — not measurements.',
          'The correct three are: Clock, Checklist, and Bar Chart.',
        ],
      },
      {
        id: 'r1_mathlock',
        title: 'Combination Lock',
        narrative:
          'Three digits are hidden in this room. Find them and unlock the case.',
        params: {
          type: 'math-lock',
          digits: 3,
          answer: '472',
          clueText: 'Lamp → Bookshelf → Headphones',
        },
        dependsOn: ['r1_scavenger'],
        hints: [
          'Examine each object in the room — some display a number.',
          'Three objects carry digits: the lamp, the bookshelf, and the headphones.',
          'Lamp = 4, Bookshelf = 7, Headphones = 2. Enter: 4 7 2.',
        ],
      },
      {
        id: 'r1_classify',
        title: 'Reality vs. Measurement',
        narrative:
          'Classify each object: is it a direct reality, or a measurement of reality?',
        params: {
          type: 'classify-checklist',
          items: [
            {
              id: 'ci_clock',
              label: 'Clock reading 9:47 AM',
              description:
                'The time displayed on the clock face — a number that points to a moment.',
              isMeasurement: true,
            },
            {
              id: 'ci_feeling',
              label: 'The feeling of being behind',
              description:
                'An internal sense of urgency and stress — felt, not counted.',
              isMeasurement: false,
            },
            {
              id: 'ci_score',
              label: 'Productivity score: 78%',
              description:
                'A numeric rating of output — a frame placed over experience.',
              isMeasurement: true,
            },
            {
              id: 'ci_weight',
              label: 'The weight of unfinished tasks',
              description:
                'The emotional burden of a long to-do list — felt, not calculated.',
              isMeasurement: false,
            },
            {
              id: 'ci_deadline',
              label: 'Deadline: Friday 5 PM',
              description:
                'A point in time set by an authority — a marker, not the experience.',
              isMeasurement: true,
            },
            {
              id: 'ci_overwhelm',
              label: 'Sense of overwhelm',
              description:
                'A feeling that things are unmanageable — a lived state, not a metric.',
              isMeasurement: false,
            },
          ],
        },
        hints: [
          'Ask: can you put a number on it, or do you only feel it?',
          'Measurements are external and quantifiable. Realities are internal and felt.',
          'Measurements: Clock, Productivity Score, Deadline. Realities: Feeling Behind, Weight of Tasks, Overwhelm.',
        ],
      },
    ],
  },

  // ── Room 2: The Invisible Scoreboard ──────────────────────────────────────
  {
    id: 'room2',
    title: 'The Invisible Scoreboard',
    narrative: "You can't win a scoreboard you didn't agree to.",
    backgroundImage: room2Img,
    completionRule: { type: 'all' },
    puzzles: [
      {
        id: 'r2_scoreboard',
        title: 'The Scoreboard',
        narrative:
          'Drag the bars to optimize your scores. Notice what changes when you do.',
        params: {
          type: 'd3-scoreboard',
          requiredDrags: 3,
          metrics: [
            { id: 'productivity', label: 'Productivity', initialValue: 72, linkedTo: 'social', linkFactor: 0.7 },
            { id: 'social', label: 'Social Time', initialValue: 45, linkedTo: 'productivity', linkFactor: 0.7 },
            { id: 'fitness', label: 'Fitness', initialValue: 61, linkedTo: 'income', linkFactor: 0.5 },
            { id: 'income', label: 'Income', initialValue: 88, linkedTo: 'fitness', linkFactor: 0.5 },
            { id: 'balance', label: 'Life Balance', initialValue: 34 },
          ],
        },
        hints: [
          'Drag the bar handles up or down to change your scores.',
          'Some metrics are linked — improving one reduces another.',
          'Drag any 3 bars to unlock the Accept button.',
        ],
      },
      {
        id: 'r2_sequence',
        title: 'The Access Code',
        narrative:
          'The scoreboard terminal has a 4-icon access code. Enter the correct sequence.',
        params: {
          type: 'icon-sequence',
          slots: 4,
          palette: ['circle-question', 'chart-line', 'users', 'heart-pulse', 'chart-bar', 'arrow-trend-up'],
          correctSequence: ['circle-question', 'chart-line', 'users', 'heart-pulse'],
          clueText: 'Question → Analyze → Connect → Vitals',
        },
        dependsOn: ['r2_scoreboard'],
        hints: [
          'The sequence follows a logical progression of steps.',
          'First you question, then you analyze, then you involve others, then you check vitals.',
          'The correct sequence is: Question → Chart Line → Users → Heart Pulse.',
        ],
      },
      {
        id: 'r2_frame',
        title: 'The Final Choice',
        narrative:
          'The scoreboard offers three actions. Only one leads out of the frame.',
        params: {
          type: 'frame-question',
          options: [
            {
              id: 'optimize',
              label: 'Optimize the score',
              description: 'Work harder. Improve the numbers. Stay inside the frame.',
              icon: 'arrow-trend-up',
            },
            {
              id: 'compare',
              label: 'Compare to others',
              description: 'Find where you rank. Let the scoreboard tell you who you are.',
              icon: 'users',
            },
            {
              id: 'question',
              label: 'Question who chose this',
              description: 'Ask who decided these metrics matter — and why you accepted them.',
              icon: 'circle-question',
            },
          ],
          correctOptionId: 'question',
          grantsItem: 'frame_key',
        },
        dependsOn: ['r2_sequence'],
        hints: [
          'Not all options lead forward. Two are traps that keep you in the frame.',
          'Optimizing and comparing both accept the scoreboard as real. One option does not.',
          'Choose: "Question who chose this" — and receive the key.',
        ],
      },
    ],
  },

  // ── Room 3: The Mirror Frame ───────────────────────────────────────────────
  {
    id: 'room3',
    title: 'The Mirror Frame',
    narrative: "The exit appears when you name what's happening.",
    backgroundImage: room3Img,
    completionRule: { type: 'all' },
    puzzles: [
      {
        id: 'r3_drag',
        title: 'Assemble the Sigil',
        narrative:
          'Place the three glyphs you collected into their correct positions.',
        params: {
          type: 'drag-assembly',
          slots: 3,
          correctOrder: ['glyph_c', 'glyph_a', 'glyph_b'],
          requiredItems: ['glyph_a', 'glyph_b', 'glyph_c'],
          grantsItem: 'sigil_complete',
        },
        dependsOnInventory: ['glyph_a', 'glyph_b', 'glyph_c'],
        hints: [
          'Drag glyphs from your inventory into the three numbered slots.',
          'The glyphs were found in sequence: first the deadline, then the clock, then the journal.',
          'Correct order: Slot 1 = Glyph C (Deadline), Slot 2 = Glyph A (Clock), Slot 3 = Glyph B (Journal).',
        ],
      },
      {
        id: 'r3_phrase',
        title: 'The Phrase',
        narrative: 'Arrange the words into the sentence that names the escape.',
        params: {
          type: 'phrase-assembly',
          words: ['I', 'can', 'choose', 'the', 'measurement', 'frame', 'am', 'not', 'be', 'my'],
          correctPhrase: 'i can choose the measurement',
        },
        dependsOn: ['r3_drag'],
        hints: [
          'Select words in order to build a sentence. Click a word to place it; click placed words to remove.',
          'The sentence begins with the subject, then ability, then action.',
          'The phrase is: I · can · choose · the · measurement.',
        ],
      },
      {
        id: 'r3_final',
        title: 'Speak the Phrase',
        narrative:
          'Type the sentence you assembled. Say it aloud — then type it here.',
        params: {
          type: 'final-input',
          acceptedPhrase: 'i can choose the measurement',
          placeholder: 'Type the phrase…',
        },
        dependsOn: ['r3_phrase', 'r3_drag'],
        dependsOnInventory: ['frame_key'],
        hints: [
          'Type what you have learned. The answer is in the phrase you assembled.',
          'Use lowercase. No punctuation needed.',
          'Type: i can choose the measurement',
        ],
      },
    ],
  },
];
